import type {
  PlannedTrainingDTO,
  StartedTrainingResumeState,
} from '../context/StartedTrainingContext';
import { API_BASE } from './apiBase';
import { authFetch, authFetchJSON } from './authFetch';

export const STARTED_TRAINING_SNAPSHOT_KEY = 'startedTrainingSnapshot:v1';
export const STARTED_TRAINING_SNAPSHOT_EVENT = 'startedTraining:snapshot';

export type StartedTrainingSnapshot = {
  plannedId: string | null;
  savedAt: number;
  training: PlannedTrainingDTO;
  resumeState: StartedTrainingResumeState;
};

function normalizeSnapshot(raw: any): StartedTrainingSnapshot | null {
  if (!raw || typeof raw !== 'object') return null;
  const plannedId =
    typeof raw.plannedId === 'string' && raw.plannedId.trim()
      ? raw.plannedId
      : null;
  const training = raw.training;
  const resumeState = raw.resumeState;
  if (!training || !resumeState) return null;
  const savedAtRaw = raw.savedAt;
  let savedAt = Date.now();
  if (typeof savedAtRaw === 'number' && Number.isFinite(savedAtRaw)) {
    savedAt = savedAtRaw;
  } else if (typeof savedAtRaw === 'string') {
    const asNum = Number(savedAtRaw);
    if (Number.isFinite(asNum)) {
      savedAt = asNum;
    } else {
      const parsed = Date.parse(savedAtRaw);
      if (!Number.isNaN(parsed)) savedAt = parsed;
    }
  }
  return {
    plannedId,
    savedAt,
    training,
    resumeState,
  };
}

function emitSnapshotEvent() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(STARTED_TRAINING_SNAPSHOT_EVENT));
}

export function readStartedTrainingSnapshot(): StartedTrainingSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STARTED_TRAINING_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const norm = normalizeSnapshot(parsed);
    return norm;
  } catch {
    return null;
  }
}

export function writeStartedTrainingSnapshot(snapshot: StartedTrainingSnapshot) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      STARTED_TRAINING_SNAPSHOT_KEY,
      JSON.stringify(snapshot)
    );
  } catch {
    return;
  }
  emitSnapshotEvent();
}

export function clearStartedTrainingSnapshot() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STARTED_TRAINING_SNAPSHOT_KEY);
  } catch {
    return;
  }
  emitSnapshotEvent();
}

export async function fetchStartedTrainingSnapshotRemote(): Promise<{
  ok: boolean;
  snapshot: StartedTrainingSnapshot | null;
}> {
  const res = await authFetchJSON<StartedTrainingSnapshot | null>(
    `${API_BASE}/api/started-training`
  );
  if (!res.ok) return { ok: false, snapshot: null };
  const normalized = normalizeSnapshot(res.data);
  return { ok: true, snapshot: normalized };
}

export async function saveStartedTrainingSnapshotRemote(
  snapshot: StartedTrainingSnapshot
): Promise<boolean> {
  const res = await authFetchJSON(`${API_BASE}/api/started-training`, {
    method: 'PUT',
    body: JSON.stringify(snapshot),
  });
  return res.ok;
}

export async function clearStartedTrainingSnapshotRemote(): Promise<boolean> {
  const res = await authFetch(`${API_BASE}/api/started-training`, {
    method: 'DELETE',
  });
  return res.ok;
}
